import { AudioBus } from './AudioBus';

/** Procedural wind + footsteps / climb / lever / gate one-shots on the shared bus. */
export class AmbientAudio {
  private bus = AudioBus.get();
  private started = false;
  private titleStarted = false;
  private stepPhase = 0;
  private lastStep = 0;

  async startTitleBed(): Promise<void> {
    if (this.titleStarted) return;
    this.titleStarted = true;
    await this.bus.unlock();
    this.ensureWind();
    this.bus.setTitleBed(true);
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    await this.bus.unlock();
    this.ensureWind();
    this.bus.setTitleBed(false);
  }

  getContext(): AudioContext | null {
    return this.bus.ctx;
  }

  /** Call each frame with player locomotion state. */
  updateFootsteps(
    dt: number,
    opts: { grounded: boolean; speed: number; climbing: boolean; running: boolean },
  ): void {
    if (!this.bus.isUnlocked()) return;
    if (opts.climbing) {
      this.stepPhase += dt * 4.2;
      if (this.stepPhase - this.lastStep > 1) {
        this.lastStep = this.stepPhase;
        this.playClimbScrape();
      }
      return;
    }
    if (!opts.grounded || opts.speed < 0.45) {
      this.stepPhase = 0;
      this.lastStep = 0;
      return;
    }
    const rate = opts.running ? 5.2 : 3.4;
    this.stepPhase += dt * rate * Math.min(1.6, opts.speed * 0.35);
    if (this.stepPhase - this.lastStep > 1) {
      this.lastStep = this.stepPhase;
      this.playFootstep(opts.running);
    }
  }

  playFootstep(heavy = false): void {
    this.blip({
      freq: heavy ? 90 : 120,
      freqEnd: heavy ? 55 : 70,
      dur: heavy ? 0.09 : 0.07,
      gain: heavy ? 0.22 : 0.14,
      type: 'triangle',
      noise: 0.35,
    });
  }

  playClimbScrape(): void {
    this.blip({
      freq: 180,
      freqEnd: 90,
      dur: 0.11,
      gain: 0.16,
      type: 'sawtooth',
      noise: 0.55,
      filterFreq: 700,
    });
  }

  playLever(): void {
    this.blip({ freq: 140, freqEnd: 70, dur: 0.18, gain: 0.28, type: 'square', noise: 0.4 });
    window.setTimeout(() => {
      this.blip({ freq: 60, freqEnd: 40, dur: 0.25, gain: 0.2, type: 'triangle', noise: 0.5 });
    }, 120);
  }

  playGate(): void {
    this.blip({
      freq: 55,
      freqEnd: 35,
      dur: 0.55,
      gain: 0.32,
      type: 'sawtooth',
      noise: 0.65,
      filterFreq: 400,
    });
    window.setTimeout(() => {
      this.blip({ freq: 90, freqEnd: 50, dur: 0.35, gain: 0.18, type: 'triangle', noise: 0.4 });
    }, 280);
  }

  playLand(): void {
    this.blip({ freq: 80, freqEnd: 40, dur: 0.12, gain: 0.24, type: 'triangle', noise: 0.45 });
  }

  private ensureWind(): void {
    if (!this.bus.ctx || !this.bus.ambientGain) return;
    // Avoid double-stacking wind if title bed already ran
    if ((this.bus.ambientGain as unknown as { _qkWind?: boolean })._qkWind) return;
    (this.bus.ambientGain as unknown as { _qkWind?: boolean })._qkWind = true;
    this.makeNoisePad(0.55, 180, 0.8);
    this.makeNoisePad(0.3, 520, 1.4);
    this.makeTone(98, 0.012);
    this.makeTone(146.8, 0.008);
  }

  private makeNoisePad(gain: number, freq: number, q: number): void {
    const ctx = this.bus.ctx;
    const dest = this.bus.ambientGain;
    if (!ctx || !dest) return;
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(filter);
    filter.connect(g);
    g.connect(dest);
    src.start();
  }

  private makeTone(freq: number, gain: number): void {
    const ctx = this.bus.ctx;
    const dest = this.bus.ambientGain;
    if (!ctx || !dest) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    osc.connect(g);
    g.connect(dest);
    osc.start();
  }

  private blip(opts: {
    freq: number;
    freqEnd: number;
    dur: number;
    gain: number;
    type: OscillatorType;
    noise?: number;
    filterFreq?: number;
  }): void {
    const ctx = this.bus.ctx;
    const dest = this.bus.sfxGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, opts.freqEnd), now + opts.dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(opts.gain, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + opts.dur);
    osc.connect(g);
    g.connect(dest);
    osc.start(now);
    osc.stop(now + opts.dur + 0.02);

    if (opts.noise && opts.noise > 0) {
      const n = Math.floor(ctx.sampleRate * opts.dur);
      const buf = ctx.createBuffer(1, n, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = opts.filterFreq ?? 900;
      const ng = ctx.createGain();
      ng.gain.value = opts.gain * opts.noise;
      src.connect(f);
      f.connect(ng);
      ng.connect(dest);
      src.start(now);
    }
  }
}
